import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, InputNumber, Select, DatePicker, Button, Card, Typography, message, Space } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ncfSequenceService, ncfTypeService, type NcfSequence, type NcfType, type CreateNcfSequenceRequest } from '../../../services/ncfService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const { Title } = Typography;
const { Option } = Select;

interface NcfSequenceFormValues {
  ncf_type_id: number;
  prefix: string;
  start_number: number;
  end_number: number;
  authorization_number: string;
  valid_from: dayjs.Dayjs;
  valid_until: dayjs.Dayjs;
}

const NcfSequenceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form] = Form.useForm<NcfSequenceFormValues>();
  const [ncfTypes, setNcfTypes] = useState<NcfType[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    ncfTypeService.getActive().then(setNcfTypes).catch(() => {
      message.error('Error al cargar los tipos NCF');
    });
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    
    setLoading(true);
    ncfSequenceService.getById(Number(id))
      .then((sequence: NcfSequence) => {
        form.setFieldsValue({
          ncf_type_id: sequence.ncf_type_id,
          prefix: sequence.prefix,
          start_number: sequence.start_number,
          end_number: sequence.end_number,
          authorization_number: sequence.authorization_number,
          valid_from: dayjs(sequence.valid_from),
          valid_until: dayjs(sequence.valid_until),
        });
      })
      .catch(() => {
        message.error('Error al cargar la secuencia');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, isEdit, form]);

  const handleSubmit = async (values: NcfSequenceFormValues) => {
    setSubmitting(true);
    try {
      if (isEdit && id) {
        await ncfSequenceService.update(Number(id), {
          authorization_number: values.authorization_number,
          valid_from: values.valid_from.format('YYYY-MM-DD'),
          valid_until: values.valid_until.format('YYYY-MM-DD'),
        });
        message.success('Secuencia actualizada correctamente');
      } else {
        const data: CreateNcfSequenceRequest = {
          ncf_type_id: values.ncf_type_id,
          prefix: values.prefix,
          start_number: values.start_number,
          end_number: values.end_number,
          authorization_number: values.authorization_number,
          valid_from: values.valid_from.format('YYYY-MM-DD'),
          valid_until: values.valid_until.format('YYYY-MM-DD'),
        };
        await ncfSequenceService.create(data);
        message.success('Secuencia creada correctamente');
      }
      navigate('/ncf-sequences');
    } catch {
      message.error('Error al guardar la secuencia');
    } finally {
      setSubmitting(false);
    }
  };

  const validateStartEnd = (_: unknown, value: number) => {
    const startNumber = form.getFieldValue('start_number');
    if (value && startNumber && value <= startNumber) {
      return Promise.reject(new Error('Debe ser mayor al número inicial'));
    }
    return Promise.resolve();
  };

  const validateDates = (_: unknown, value: dayjs.Dayjs) => {
    const validFrom = form.getFieldValue('valid_from');
    if (value && validFrom && value.isBefore(validFrom)) {
      return Promise.reject(new Error('Debe ser posterior a vigencia desde'));
    }
    return Promise.resolve();
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Secuencias NCF', to: '/ncf-sequences' }, { label: isEdit ? 'Editar Secuencia' : 'Nueva Secuencia' }]} />
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col">
              <Title level={4}>
                {isEdit ? 'Editar' : 'Nueva'} Secuencia NCF
              </Title>
            </div>
          </div>
        </div>

        <Card loading={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              start_number: 1,
              end_number: 1000,
            }}
          >
            <Form.Item
              label="Tipo NCF"
              name="ncf_type_id"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Select disabled={isEdit} placeholder="Seleccionar...">
                {ncfTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.code} — {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Prefijo"
              name="prefix"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Input disabled={isEdit} placeholder="B02" />
            </Form.Item>

            <Space size="large" style={{ width: '100%' }} direction="vertical">
              <Space size="large">
                <Form.Item
                  label="Número Inicial"
                  name="start_number"
                  rules={[{ required: true, message: 'Requerido' }]}
                >
                  <InputNumber disabled={isEdit} min={1} style={{ width: 200 }} />
                </Form.Item>

                <Form.Item
                  label="Número Final"
                  name="end_number"
                  rules={[
                    { required: true, message: 'Requerido' },
                    { validator: validateStartEnd },
                  ]}
                >
                  <InputNumber disabled={isEdit} min={1} style={{ width: 200 }} />
                </Form.Item>
              </Space>
            </Space>

            <Form.Item
              label="No. Autorización DGII"
              name="authorization_number"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Input placeholder="Autorización DGII" />
            </Form.Item>

            <Space size="large">
              <Form.Item
                label="Vigencia Desde"
                name="valid_from"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: 200 }} />
              </Form.Item>

              <Form.Item
                label="Vigencia Hasta"
                name="valid_until"
                rules={[
                  { required: true, message: 'Requerido' },
                  { validator: validateDates },
                ]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: 200 }} />
              </Form.Item>
            </Space>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  Guardar
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => navigate('/ncf-sequences')}
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default NcfSequenceFormPage;
